import {Component, OnInit} from '@angular/core';
import {BlockStatementAbstractComponent} from '../block-statement-abstract.component';

import {ValuableBenefitEnum} from '@core-app/enums/other-valuable-benefits.enum';

import type {ValuableBenefitType} from '@core-app/types/other-valuable-benefits.type';
import type {ValuableBenefitsData} from '@core-app/types/pillar.type';

@Component({
    selector: 'core-other-valuable-benefits',
    templateUrl: './block-other-valuable-benefits.component.html',
    standalone: false,
})
export class BlockOtherValuableBenefitsComponent
    extends BlockStatementAbstractComponent
    implements OnInit {
    ValuableBenefitsData: ValuableBenefitsData = {
        [ValuableBenefitEnum.FOOD_DELIVERY_DISCOUNTS]: {
            heading: 'Food Delivery Discounts',
            description:
                'With a FREE DashPass membership, you can order groceries, meals, and more on DoorDash with $0 delivery and reduced service fees.',
            link: 'https://doordash.com',
        },
        [ValuableBenefitEnum.FITNESS_DISCOUNTS]: {
            heading: 'Virtual Classes & Gym Discounts',
            description:
                'Burnalong offers 35,000 live and on-demand classes for you and four family members. Attend nutrition programs, cooking classes, exercise classes, and more!',
            link: 'https://join.burnalong.com/solv',
        },
        [ValuableBenefitEnum.DAY_CARE_SUPPORT]: {
            heading: 'Day Care Support',
            description:
                'Whether you have children, pets, parents, or aging grandparents, a FREE premium membership to Care.com provides access to the world’s largest network of care providers.',
            link: 'https://solv.care.com/',
        },
        [ValuableBenefitEnum.FAMILY_BUILDING_SUPPORT]: {
            heading: 'Family Building Support',
            description:
                'We’ve partnered with BenefitBump to offer personalized pregnancy, fertility, surrogacy, and adoption support, so you have the care you need on your path to parenthood—and beyond! Use Code: SOLV at login',
            link: 'https://mybenefitbump.com/public/sign-up',
        },
        [ValuableBenefitEnum.CAREER_DEVELOPMENT]: {
            heading: 'Career Development',
            description:
                'Grow your career through training programs, mentorships, and opportunities to learn beyond your role.',
            link: 'https://solvenergyteam.sharepoint.com/sites/TalentDevelopmentIntranet',
            linkLabel: 'Visit Talent Development',
        },
        [ValuableBenefitEnum.TUITION_REIMBURSEMENT_ADMIN]: {
            heading: 'Tuition Reimbursement',
            description:
                'If you want to further your education, we’re on board! After one year of employment, full-time administrative employees are eligible for educational reimbursement.',
            link: 'mailto:talentdevelopment@solvenergy.com',
            linkLabel: 'Email Talent Development',
        },
        [ValuableBenefitEnum.MSA_FINANCIAL_WELLNESS]: {
            heading: 'Financial Wellness',
            description:
                'With My Secure Advantage (MSA), gain confidence in managing your finances at any stage in life. From student loans to retirement planning, MSA offers guidance and support. Connect with a confidential Money Coach today for personalized advice and strategies.',
            link: 'https://solv.mysecureadvantage.com',
        },
        [ValuableBenefitEnum.EMPLOYEE_GIVING]: {
            heading: 'Employee Giving',
            description:
                'Through various donation and volunteer opportunities, employees have every opportunity to make a lasting impact to our local neighbors and non-profit organizations they are passionate about.',
            link: 'https://solvenergy.wespire.com/app/landing/sso',
            linkLabel: 'Visit the ripple effect',
        },
    };

    isBlankPageNeededForPdf!: boolean;
    valuableBenefits: ValuableBenefitType[] = [];

    override ngOnInit(): void {
        super.ngOnInit();
        this.isBlankPageNeededForPdf = this.trsService.showPdfBlankPage1();

        this.initializeTiles();
    }

    initializeTiles(): void {
        // The value is either a "0" or "1";
        // enabled if it's a "1"
        const isEnabled = (value: string) => !!+value;

        this.valuableBenefits = [
            ValuableBenefitEnum.MSA_FINANCIAL_WELLNESS,
            ValuableBenefitEnum.CAREER_DEVELOPMENT,
            ValuableBenefitEnum.FAMILY_BUILDING_SUPPORT,
            ValuableBenefitEnum.DAY_CARE_SUPPORT,
            ValuableBenefitEnum.EMPLOYEE_GIVING,
            ValuableBenefitEnum.FITNESS_DISCOUNTS,
            ValuableBenefitEnum.FOOD_DELIVERY_DISCOUNTS,
            ValuableBenefitEnum.TUITION_REIMBURSEMENT_ADMIN,
        ].filter((benefit) =>
            isEnabled(this.auth.user.data[benefit])
        ) as ValuableBenefitType[];

        // 2025-04-11 client request to always show the Employee Giving benefit
        if (!this.valuableBenefits.includes(ValuableBenefitEnum.EMPLOYEE_GIVING)) {
            this.valuableBenefits.push(ValuableBenefitEnum.EMPLOYEE_GIVING);
        }

        this.valuableBenefits = this.sortValuableBenefitsOrder(
            this.valuableBenefits
        );
    }

    /**
     * Sorts the `benefits` array according to the predefined `order`.
     *
     * @param {ValuableBenefitType} benefits - The array of benefits to be sorted
     * @returns {ValuableBenefitType[]} - The sorted array of benefits
     */
    sortValuableBenefitsOrder(
        benefits: ValuableBenefitType[]
    ): ValuableBenefitType[] {
        const order: ValuableBenefitType[] = [
            ValuableBenefitEnum.MSA_FINANCIAL_WELLNESS,
            ValuableBenefitEnum.CAREER_DEVELOPMENT,
            ValuableBenefitEnum.TUITION_REIMBURSEMENT_ADMIN,
            ValuableBenefitEnum.FAMILY_BUILDING_SUPPORT,
            ValuableBenefitEnum.DAY_CARE_SUPPORT,
            ValuableBenefitEnum.EMPLOYEE_GIVING,
            ValuableBenefitEnum.FITNESS_DISCOUNTS,
            ValuableBenefitEnum.FOOD_DELIVERY_DISCOUNTS,
        ];

        const lookup = Object.fromEntries(
            order.map((benefit, index) => [benefit, index])
        );

        // Sort `benefits` based on `order`
        return benefits.sort((a, b) => lookup[a] - lookup[b]);
    }
}
